// Minimal read-only ZIP reader - enough to pull named parts out of an .xlsx, which is a ZIP of XML.
// Only the two compression methods Excel emits are supported: stored and deflate.

import { InvalidWorkbookError } from "./InvalidWorkbookError";

const END_OF_CENTRAL_DIRECTORY = 0x06054b50;
const CENTRAL_FILE_HEADER = 0x02014b50;
const LOCAL_FILE_HEADER = 0x04034b50;

const STORED = 0;
const DEFLATED = 8;

// The end record is last, but a trailing comment of up to 64KB can follow it.
const MAX_COMMENT_LENGTH = 0xffff;

interface ZipEntry {
  method: number;
  compressedSize: number;
  localHeaderOffset: number;
}

const findEndOfCentralDirectory = (view: DataView): number => {
  const earliest = Math.max(0, view.byteLength - MAX_COMMENT_LENGTH - 22);

  for (let offset = view.byteLength - 22; offset >= earliest; offset -= 1) {
    if (view.getUint32(offset, true) === END_OF_CENTRAL_DIRECTORY) return offset;
  }

  throw new InvalidWorkbookError();
};

const inflate = async (compressed: Uint8Array): Promise<Uint8Array> => {
  if (typeof DecompressionStream === "undefined") {
    throw new InvalidWorkbookError("This browser cannot decompress the file");
  }

  const stream = new DecompressionStream("deflate-raw");
  const writer = stream.writable.getWriter();
  // Not awaited before reading: the stream buffers, and awaiting a write that only drains once the
  // reader starts would deadlock.
  writer.write(compressed);
  writer.close();

  const reader = stream.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;

  for (;;) {
    // Chunks arrive in order, so each read depends on the last.
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    total += value.length;
  }

  const result = new Uint8Array(total);
  let position = 0;
  chunks.forEach((chunk) => {
    result.set(chunk, position);
    position += chunk.length;
  });

  return result;
};

export class ZipArchive {
  private readonly view: DataView;

  private readonly bytes: Uint8Array;

  private readonly entries = new Map<string, ZipEntry>();

  constructor(data: ArrayBuffer) {
    this.bytes = new Uint8Array(data);
    this.view = new DataView(data);

    const end = findEndOfCentralDirectory(this.view);
    const count = this.view.getUint16(end + 10, true);
    const decoder = new TextDecoder();
    let offset = this.view.getUint32(end + 16, true);

    for (let index = 0; index < count; index += 1) {
      if (offset + 46 > this.bytes.length || this.view.getUint32(offset, true) !== CENTRAL_FILE_HEADER) break;

      const nameLength = this.view.getUint16(offset + 28, true);
      const extraLength = this.view.getUint16(offset + 30, true);
      const commentLength = this.view.getUint16(offset + 32, true);
      const name = decoder.decode(this.bytes.subarray(offset + 46, offset + 46 + nameLength));

      this.entries.set(name, {
        method: this.view.getUint16(offset + 10, true),
        compressedSize: this.view.getUint32(offset + 20, true),
        localHeaderOffset: this.view.getUint32(offset + 42, true),
      });

      offset += 46 + nameLength + extraLength + commentLength;
    }
  }

  has(name: string): boolean {
    return this.entries.has(name);
  }

  names(): string[] {
    return Array.from(this.entries.keys());
  }

  async read(name: string): Promise<Uint8Array | null> {
    const entry = this.entries.get(name);
    if (!entry) return null;

    const header = entry.localHeaderOffset;
    if (this.view.getUint32(header, true) !== LOCAL_FILE_HEADER) throw new InvalidWorkbookError();

    // The local header repeats the name and extra fields, and its extra field length can differ
    // from the central directory's - so the data offset has to come from here.
    const nameLength = this.view.getUint16(header + 26, true);
    const extraLength = this.view.getUint16(header + 28, true);
    const start = header + 30 + nameLength + extraLength;
    const raw = this.bytes.subarray(start, start + entry.compressedSize);

    if (entry.method === STORED) return raw;
    if (entry.method === DEFLATED) return inflate(raw);

    throw new InvalidWorkbookError(`Unsupported compression method ${entry.method}`);
  }

  async readText(name: string): Promise<string | null> {
    const bytes = await this.read(name);
    return bytes === null ? null : new TextDecoder().decode(bytes);
  }
}
